// App.js
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, useLocation} from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import theme from './theme';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import { Box, Skeleton } from '@mui/material';
import './App.css'

// ----------------- Lazy Loading -----------------
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return await componentImport();
    }
  });

// Lazy load components
const WorkflowPage = lazy(() => import('./pages/WorkflowPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const IntershipForm = lazy(() => import('./pages/intershipform'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ServiceConsultancy = lazy(() => import('./pages/services/Consultancy'));
const ServiceAppDevelopment = lazy(() => import('./pages/services/AppDevelopment'));
const ServiceAutomation = lazy(() => import('./pages/services/Automation'));
const ServiceSaaS = lazy(() => import('./pages/services/SaaS'));
const ServiceMVP = lazy(() => import('./pages/services/MVP'));
const Services = lazy(() => import('./pages/ServicesPage'));
const PaymentForm = lazy(() => import('./pages/PaymentForm'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const Team = lazy(() => import('./pages/teamMembers'));
const Internships = lazy(() => import('./pages/services/Internships'));
const Gallery = lazy(() => import('./pages/Gallery'));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail'));
const BreadcrumbsBar = lazy(() => import('./components/BreadcrumbsBar'));
const FloatingButton = lazy(() => import('./components/ChatBot/FloatingButton'));

const Education = lazy(() => import('./pages/services/Education'));
const Exam = lazy(() => import('./pages/services/Exam'));
const More = lazy(() => import('./pages/services/More'));

const CertificatePortal = lazy(() => import('./pages/CertificatePortal'));
const CertificatePortalDetail = lazy(() => import('./pages/CertificatePortalDetail'));
const Projects = lazy(() => import('./pages/Project'));
const Logo = lazy(() => import('./pages/Logo'));

const CancellationandRefund = lazyWithRetry(() => import("./policy/CancellationandRefund"));
const ContactUs = lazyWithRetry(() => import("./policy/ContactUs"));
const PrivacyPolicy = lazyWithRetry(() => import("./policy/PrivacyPolicy"));
const ShippingandDelivery = lazyWithRetry(() => import("./policy/ShippingandDelivery"));
const TermsandConditions = lazyWithRetry(() => import("./policy/TermsandConditions"));
const InternList = lazyWithRetry(()=>import("./intern/internList"));
const Intern = lazyWithRetry(()=>import("./intern/intern"));

// Skeleton loading component
const PageSkeleton = () => (
  <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
    <Skeleton variant="rectangular" height={300} sx={{ mb: 3, borderRadius: 2, width: '100%' }} />
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <Skeleton variant="text" height={60} width="80%" sx={{ mb: 3 }} />
      <Skeleton variant="text" height={40} width="60%" sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ flex: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ flex: 1, borderRadius: 1 }} />
      </Box>
      <Skeleton variant="text" height={30} width="90%" />
      <Skeleton variant="text" height={30} width="70%" />
    </Box>
  </Box>
);

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Smooth scroll to top with behavior
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    
    // Alternative instant scroll (uncomment if you prefer instant)
    // window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {/* Scroll to top on route change */}
        <ScrollToTop />
        
        <Routes>
          {/* =========================
              NORMAL WEBSITE MODE
              ========================= */}
          <Route
            path="/*"
            element={
              <>
                <Navbar />
                <Box 
                  component="main" 
                  sx={{ 
                    minHeight: '100vh',
                    position: 'relative',
                    '& img[data-src]': { 
                      opacity: 0, 
                      transition: 'opacity 0.5s ease-in',
                      filter: 'blur(4px)',
                    },
                    '& img.loaded': { 
                      opacity: 1,
                      filter: 'blur(0)',
                    }
                  }}
                >
                  <Suspense fallback={
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      minHeight: 'calc(100vh - 200px)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3 
                    }}>
                      <PageSkeleton />
                    </Box>
                  }>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/workflow" element={<WorkflowPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/clients" element={<ClientsPage />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/team" element={<Team />} />
                      <Route path="/team/:id" element={<TeamMemberDetail />} />
                      <Route path="/gallery" element={<Gallery />} />
                      <Route path="/projects" element={<Projects/>}/>
                      <Route path="/projects/:slug" element={<Projects />} />
                      <Route path="/logo" element={<Logo/>}/> 

                      <Route path="/Cancellation-and-Refund" element={<CancellationandRefund />} />
                      <Route path="/Contact-Us" element={<ContactUs />} />
                      <Route path="/Privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/Shipping-and-delivery" element={<ShippingandDelivery />} />
                      <Route path="/Terms-and-conditions" element={<TermsandConditions />} />
            

                      <Route path="/services/consultancy" element={<ServiceConsultancy />} />
                      <Route path="/services/app-development" element={<ServiceAppDevelopment />} />
                      <Route path="/services/automation" element={<ServiceAutomation />} />
                      <Route path="/services/saas" element={<ServiceSaaS />} />
                      <Route path="/services/mvp" element={<ServiceMVP />} />
                      <Route path="/services/education" element={<Education />} />
                      <Route path="/services/more" element={<More />} />
                      <Route path="/services/exam" element={<Exam />} />
                      <Route path="/services/internships" element={<Internships />} />
                      <Route path="/internship" element={<Internships />} />

                      
                      <Route path="/services/internships/form" element={<IntershipForm />} />
                      <Route path="/services/internships/form/payment" element={<PaymentForm />} />


                      <Route path="/internship/certificate-portal" element={<CertificatePortal />} />
                      <Route path="/internship/certificate-portal/:registrationId" element={<CertificatePortalDetail />} />
                      <Route path="/intern" element={<InternList/>}/>
                      <Route path="/intern/:registrationId" element={<Intern/>}/>

                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </Box>
                
                <Suspense fallback={null}>
                  <FloatingButton />
                  <BreadcrumbsBar />
                </Suspense>
                
                <Footer />
              </>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;